import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    ShieldCheck, Loader2, User, Clock, CheckCircle2, XCircle, AlertTriangle,
    PlayCircle, PauseCircle, RefreshCw, Copy, DollarSign,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_META = {
    consent_pending: { label: "Consent Pending", icon: Clock, cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
    payment_pending: { label: "Payment Pending", icon: DollarSign, cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    ready_for_processing: { label: "Ready for Processing", icon: PlayCircle, cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
    in_progress: { label: "In Progress", icon: RefreshCw, cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
    completed: { label: "Completed", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    review_required: { label: "Review Required", icon: AlertTriangle, cls: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
    cancelled: { label: "Cancelled", icon: PauseCircle, cls: "bg-slate-500/15 text-slate-600 dark:text-slate-400" },
};
const StatusPill = ({ status }) => {
    const m = STATUS_META[status] || { label: status || "—", icon: Clock, cls: "bg-muted text-muted-foreground" };
    const Icon = m.icon;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${m.cls}`}>
            <Icon size={10} /> {m.label}
        </span>
    );
};

const FILTERS = [
    { key: "all", label: "All" },
    { key: "consent_pending", label: "Consent" },
    { key: "payment_pending", label: "Payment" },
    { key: "ready_for_processing", label: "Ready" },
    { key: "in_progress", label: "In Progress" },
    { key: "review_required", label: "Review" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
];

export default function PersonalBackgroundQueue() {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [busyId, setBusyId] = useState(null);
    const [actionTarget, setActionTarget] = useState(null);
    const [providerRef, setProviderRef] = useState("");
    const [internalNote, setInternalNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = statusFilter === "all"
                ? "/admin/personal-background"
                : `/admin/personal-background?status=${statusFilter}`;
            const { data } = await api.get(url);
            setRows(data.results || []);
            setCounts(data.status_counts || {});
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [statusFilter]);
    useEffect(() => { load(); }, [load]);

    const openAction = (row, target) => {
        setActionTarget({ row, target });
        setProviderRef(row.provider_reference_id || "");
        setInternalNote("");
    };
    const runAction = async () => {
        const { row, target } = actionTarget;
        setBusyId(row.id);
        try {
            const body = { status: target };
            if (providerRef.trim()) body.provider_reference_id = providerRef.trim();
            if (internalNote.trim()) body.internal_note = internalNote.trim();
            await api.patch(`/admin/personal-background/${row.id}`, body);
            toast.success(`Updated → ${STATUS_META[target]?.label || target}.`);
            setActionTarget(null); setProviderRef(""); setInternalNote("");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusyId(null); }
    };
    const copyEmail = async (email) => {
        try { await navigator.clipboard.writeText(email); toast.success("Email copied."); }
        catch { toast.error("Could not copy."); }
    };

    return (
        <div data-testid="admin-personal-bgc-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Verification Ops</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Personal Background Checks</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Talent-initiated personal background checks (self-referential, not employer reports).
                        A separate product line from Employment Screenings — never surfaced to any employer.
                    </p>
                </div>
                <button onClick={load} disabled={loading} data-testid="pbg-refresh" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60">
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="mt-6 flex flex-wrap gap-1.5" data-testid="pbg-filter-tabs">
                {FILTERS.map((t) => {
                    const n = t.key === "all"
                        ? Object.values(counts).reduce((s, v) => s + v, 0)
                        : (counts[t.key] || 0);
                    const active = statusFilter === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setStatusFilter(t.key)}
                            data-testid={`pbg-filter-${t.key}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                active
                                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold"
                                    : "border-border bg-background hover:bg-muted text-muted-foreground"
                            }`}
                        >
                            {t.label} <span className="ml-1 tabular-nums opacity-70">{n}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-6">
                {loading ? (
                    <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div data-testid="pbg-empty" className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center">
                        <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h3 className="mt-3 text-base font-semibold text-foreground">No personal checks in this status.</h3>
                        <p className="text-sm text-muted-foreground mt-1">Talent-initiated personal background checks appear here.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((r) => (
                            <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`pbg-row-${r.id}`} className="rounded-2xl border border-border bg-card p-5">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-heading font-semibold text-foreground">{r.package_label}</h3>
                                            <StatusPill status={r.status} />
                                            {r.payment_status === "paid" && (
                                                <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-700 dark:text-emerald-400">
                                                    <CheckCircle2 size={9} className="mr-0.5" /> Paid ${(r.price_cents / 100).toFixed(2)}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="mt-1.5 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                            <div className="inline-flex items-center gap-1 min-w-0">
                                                <User size={11} />
                                                <span className="truncate">{r.candidate_name || "—"} · {r.candidate_email}</span>
                                                <button onClick={() => copyEmail(r.candidate_email)} title="Copy email"><Copy size={11} className="opacity-60 hover:opacity-100 ml-0.5" /></button>
                                            </div>
                                            <div className="inline-flex items-center gap-1"><Clock size={11} /> requested {new Date(r.requested_at).toLocaleString()}</div>
                                            {r.provider_reference_id && (
                                                <div className="inline-flex items-center gap-1"><Badge variant="outline" className="text-[10px]">Checkr ref</Badge> <code className="font-mono">{r.provider_reference_id}</code></div>
                                            )}
                                            {r.consent_at && (
                                                <div className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> consent {new Date(r.consent_at).toLocaleString()}</div>
                                            )}
                                            {r.paid_at && (
                                                <div className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> paid {new Date(r.paid_at).toLocaleString()}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 shrink-0">
                                        {r.status === "ready_for_processing" && (
                                            <button onClick={() => openAction(r, "in_progress")} disabled={busyId === r.id} data-testid={`pbg-mark-in-progress-${r.id}`} className="px-3 py-1.5 rounded-full border border-violet-500/40 bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 text-xs font-semibold hover:bg-violet-100 dark:hover:bg-violet-500/15 inline-flex items-center gap-1"><PlayCircle size={11} /> Mark In Progress</button>
                                        )}
                                        {r.status === "in_progress" && (
                                            <>
                                                <button onClick={() => openAction(r, "completed")} disabled={busyId === r.id} data-testid={`pbg-mark-completed-${r.id}`} className="px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/15 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Complete</button>
                                                <button onClick={() => openAction(r, "review_required")} disabled={busyId === r.id} data-testid={`pbg-mark-review-${r.id}`} className="px-3 py-1.5 rounded-full border border-orange-500/40 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 text-xs font-semibold hover:bg-orange-100 dark:hover:bg-orange-500/15 inline-flex items-center gap-1"><AlertTriangle size={11} /> Review</button>
                                            </>
                                        )}
                                        {["consent_pending", "payment_pending", "ready_for_processing", "in_progress", "review_required"].includes(r.status) && (
                                            <button onClick={() => openAction(r, "cancelled")} disabled={busyId === r.id} data-testid={`pbg-mark-cancelled-${r.id}`} className="px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground text-xs font-semibold hover:bg-muted inline-flex items-center gap-1"><PauseCircle size={11} /> Cancel</button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={actionTarget !== null} onOpenChange={(o) => { if (!o) { setActionTarget(null); setProviderRef(""); setInternalNote(""); } }}>
                <DialogContent data-testid="pbg-action-dialog">
                    <DialogHeader>
                        <DialogTitle>Update personal check → {STATUS_META[actionTarget?.target]?.label}</DialogTitle>
                        <DialogDescription>
                            {actionTarget?.target === "in_progress" && "Confirm you've kicked off this check in the Checkr dashboard and paste the Checkr reference id below."}
                            {actionTarget?.target === "completed" && "Confirm this Checkr run has finalized. Do NOT paste raw report contents — only record the reference id + a summary note."}
                            {actionTarget?.target === "review_required" && "Flag this for another admin to look at. Add a note describing what needs review."}
                            {actionTarget?.target === "cancelled" && "Cancel this personal check. Terminal state — the talent will need to start a fresh order to try again."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {(actionTarget?.target === "in_progress" || actionTarget?.target === "completed") && (
                            <div>
                                <label className="text-xs font-semibold text-foreground">Checkr reference id (optional)</label>
                                <Input
                                    data-testid="pbg-provider-ref-input"
                                    value={providerRef}
                                    onChange={(e) => setProviderRef(e.target.value)}
                                    placeholder="e.g. rep_xxxxxxxxx"
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-semibold text-foreground">Internal note (optional, ops-only)</label>
                            <Textarea
                                data-testid="pbg-internal-note-input"
                                rows={3}
                                value={internalNote}
                                onChange={(e) => setInternalNote(e.target.value)}
                                placeholder="Notes visible ONLY to admin ops — never to talent."
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <button type="button" onClick={() => { setActionTarget(null); setProviderRef(""); setInternalNote(""); }} data-testid="pbg-action-cancel" className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted">Cancel</button>
                        <button type="button" onClick={runAction} disabled={busyId === actionTarget?.row?.id} data-testid="pbg-action-confirm" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                            <CheckCircle2 size={12} /> Confirm
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
